import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import { set as idbSet, get as idbGet, del as idbDel } from 'idb-keyval';
import * as Sentry from '@sentry/vue';
import { clearExceptIsRead } from '@/components//Header/Notice.js';
import report_list from './report_list';
import report_indicators from './report_indicators';
import report_contents from './report_contents';
import data_tables from './data_tables';
import documents from './documents';
import business_cases from './business_cases';
import charts from './charts';
import story_board from './story_board';
import data_center from './data_center';
import assurance from './assurance';
import cursor from './cursor';
import admin from './admin';
import logs from './logs';
import billings from './billings';
import custom_products from './custom_products';
import gri2021_indicators from './gri2021_indicators';
import gri2021_report_view from './new_viewer/gri2021_report_view';
import gri2021_modal_open_status from './new_viewer/gri2021_modal_open_status';
import categoryModule from './modules/categoryModule';
import headerDropdown from './modules/headerDropdown';
import pagination from './modules/pagination';
import preview_route from './modules/preview_route';
import reportMenu from './modules/reportMenu';
import merge from 'lodash/merge'; // lodash 설치 후 상단에 추가하세요
import cloneDeep from 'lodash/cloneDeep';
import search from './modules/search';
import { authApi, publicApi } from '../utils/api';

Vue.use(Vuex);
let isRestoring = true;

// ✅ 웹P 지원 여부 확인
function isWebPSupported() {
  const ua = navigator.userAgent.toLowerCase();
  const browsers = [
    { name: 'chrome', version: 23 },
    { name: 'firefox', version: 65 },
    { name: 'edge', version: 80 },
    { name: 'safari', version: 14 },
    { name: 'opera', version: 19 },
    { name: 'samsunginternet', version: 4.0 },
    { name: 'ucbrowser', version: 12.12 }
  ];
  for (const b of browsers) {
    if (ua.includes(b.name)) {
      const m = ua.match(new RegExp(`${b.name}/?(\\d+)`));
      if (m && parseFloat(m[1]) >= b.version) return true;
    }
  }
  return false;
}

const webpSupported = isWebPSupported();

// ✅ IndexedDB에 저장할 키 목록
const INDEXED_KEYS = [
  'story_board',
  'data_tables',
  'data_center',
  'charts',
  'category',
  'business_cases',
  'documents',
  'gri2021_indicators',
  'report_contents',
  'report_indicators',
  'gri2021_report_view'
];

// ✅ localStorage에 저장할 키 목록
const LOCAL_KEYS = [
  'admin',
  'assurance',
  'authenticated',
  'backend_host',
  'billings',
  'company_name',
  'cursor',
  'custom_products',
  'e_mail',
  'frontend_host',
  'gri2021_modal_open_status',
  'headerDropdown',
  'imp_pk_code',
  'is_admin',
  'is_confirmed',
  'licenseInfo',
  'logs',
  'pagination',
  'preview_route',
  'privacy_agreement_date',
  'reportMenu',
  'selectedMethod',
  'static_host',
  'subscription',
  'tel',
  'user_id',
  'user_name',
  'report_list'
];

const store = new Vuex.Store({
  modules: {
    report_list,
    report_indicators,
    report_contents,
    data_tables,
    documents,
    business_cases,
    story_board,
    data_center,
    charts,
    assurance,
    cursor,
    admin,
    logs,
    billings,
    custom_products,
    gri2021_indicators,
    gri2021_report_view,
    gri2021_modal_open_status,
    category: categoryModule,
    headerDropdown,
    pagination,
    preview_route,
    reportMenu,
    search
  },
  state: {
    isRestoring: true,
    backend_host: process.env.VUE_APP_BACKENDHOST,
    static_host: webpSupported
      ? process.env.VUE_APP_STATICHOST
      : process.env.VUE_APP_STATICHOST_COMP,
    frontend_host: process.env.VUE_APP_FRONTHOST,
    imp_pk_code: process.env.VUE_APP_IMP_PK_CODE,
    user_id: localStorage.getItem('user_id') || '',
    e_mail: localStorage.getItem('e_mail') || '',
    user_name: localStorage.getItem('user_name') || '',
    tel: localStorage.getItem('tel') || '',
    authenticated: localStorage.getItem('authenticated') || '',
    access_token: localStorage.getItem('access_token') || '',
    subscription: localStorage.getItem('subscription') === 'true' || false,
    is_confirmed: localStorage.getItem('is_confirmed') === 'true' || false,
    is_admin: localStorage.getItem('is_admin') || null,
    company_name: localStorage.getItem('company_name') || null,
    privacy_agreement_date: localStorage.getItem('privacy_agreement_date') || null,
    selectedMethod: null,
    licenseInfo: JSON.parse(localStorage.getItem('licenseInfo')) || null
  },
  mutations: {
    setRestoring(state, value) {
      state.isRestoring = value;
    },

    setAccessToken(state, token) {
      state.access_token = token;
      if (token) {
        localStorage.setItem('access_token', token);
      } else {
        localStorage.removeItem('access_token');
      }
    },
    logout(state) {
      Object.assign(state, {
        user_id: '',
        e_mail: '',
        user_name: '',
        tel: '',
        authenticated: '',
        access_token: '',
        subscription: false,
        is_confirmed: false,
        is_admin: null,
        licenseInfo: null,
        company_name: null,
        privacy_agreement_date: null
      });
      [
        'user_id',
        'e_mail',
        'user_name',
        'tel',
        'authenticated',
        'access_token',
        'subscription',
        'is_confirmed',
        'is_admin',
        'licenseInfo',
        'alertLicenseInfo',
        'company_name',
        'privacy_agreement_date',
        'vuex_state'
      ].forEach(k => delete localStorage[k]);
      clearExceptIsRead();
      axios.defaults.headers.common['Authorization'] = '';
      location.href = '/';
    },
    auth_request(state) {
      state.authenticated = 'loading';
      localStorage.setItem('authenticated', 'loading');
    },
    auth_success(state, payload) {
      Object.entries(payload).forEach(([k, v]) => {
        state[k] = v;
        localStorage.setItem(k, v);
      });
      state.authenticated = 'success';
      localStorage.setItem('authenticated', 'success');
      // axios.defaults.headers.common['Authorization'] = `Bearer ${payload.access_token}`;
    },
    auth_error(state) {
      state.authenticated = 'error';
      localStorage.setItem('authenticated', 'error');
    },
    setLicenseInfo(state, licenseInfo) {
      state.licenseInfo = licenseInfo;
      if (licenseInfo !== null) {
        localStorage.setItem('licenseInfo', JSON.stringify(licenseInfo));
        localStorage.setItem('alertLicenseInfo', true);
      } else {
        delete localStorage.licenseInfo;
      }
    },
    setSelectedMethod(state, data) {
      state.selectedMethod = data;
    }
  },
  getters: {
    is_viewer() {
      if (!process.env.VUE_APP_REPORT_CODE) {
        const path = window.location.pathname;
        return ['/v', '/s', '/t', '/g', '/a'].some(prefix => path.startsWith(prefix));
      } else {
        return true;
      }
    },
    isMobile() {
      const userAgent = navigator.userAgent;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
      const isSmallScreen = window.innerWidth <= 768;
      const hasTouch = 'ontouchstart' in window;

      return isMobileUA || (isSmallScreen && hasTouch);
    },
    isSafari() {
      const userAgent = navigator.userAgent.toLowerCase();
      return userAgent.includes('safari') && !userAgent.includes('chrome');
    }
  },
  actions: {
    login({ commit, dispatch, state }, { e_mail, password }) {
      delete localStorage.vuex_state;

      clearExceptIsRead();

      const formData = new FormData();
      formData.append('e_mail', e_mail);
      formData.append('password', password);

      return publicApi
        .post('/login', formData, {
          withCredentials: true // 쿠키를 받아서 저장하기 위해 필수!
        })
        .then(response => {
          //response: user_id, e_mail, user_name, tel, api_key, subscription, is_confirmed, is_admin
          const user_id = response.data.user_info['user_id'];
          const e_mail = response.data.user_info['e_mail'];
          const user_name = response.data.user_info['user_name'];
          const tel = response.data.user_info['tel'];
          // let access_token = response.data.user_info['api_key'];
          const subscription = response.data.user_info['subscription'];
          const is_confirmed = response.data.user_info['is_confirmed'];
          const is_admin = response.data.user_info['is_admin'];

          const company_name = response.data.user_info['company_name'];
          const privacy_agreement_date = response.data.user_info['privacy_agreement_date'];

          if (response.data.access_token) {
            commit('setAccessToken', response.data.access_token);
          }

          commit('auth_success', {
            user_id,
            e_mail,
            user_name,
            tel,
            subscription,
            is_confirmed,
            is_admin,
            company_name,
            privacy_agreement_date
          });

          dispatch('updateLicenseInfo');
          Sentry.setUser({ is_admin: is_admin, id: user_id, email: e_mail, username: user_name });
          return response;
        })
        .catch(error => {
          throw error;
        });
    },
    logout({ commit, state }) {
      setTimeout(() => {
        clearExceptIsRead();
      }, 1);

      return authApi
        .post('/logout')
        .then(() => {
          commit('logout');
          location.href = '/';
        })
        .catch(error => {
          error.message = '[로그아웃 이슈]' + error.message;
          Sentry.captureException(error);
        });
    },
    async saveUserInfo(
      { commit, state },
      {
        user_id,
        password_old,
        password_new,
        e_mail,
        user_name,
        tel,
        subscription,
        is_confirmed,
        company_name,
        privacy_agreement_date
      }
    ) {
      const path = state.backend_host + '/update_user';

      const formData = new FormData();
      formData.append('user_id', user_id);

      if (password_old != undefined && password_new != undefined && password_new != null) {
        formData.append('password_old', password_old);
        formData.append('password_new', password_new);
      }
      if (e_mail != undefined && e_mail != null) {
        formData.append('e_mail', e_mail);
      }
      if (user_name != undefined && user_name != null) {
        formData.append('user_name', user_name);
      }
      if (tel != undefined && tel != null) {
        formData.append('tel', tel);
      }
      if (subscription != undefined && subscription != null) {
        formData.append('subscription', subscription);
      }
      if (is_confirmed != undefined && is_confirmed != null) {
        formData.append('is_confirmed', is_confirmed);
      }

      if (company_name != undefined && company_name != null) {
        formData.append('company_name', company_name);
      }
      if (privacy_agreement_date != undefined && privacy_agreement_date != null) {
        formData.append('privacy_agreement_date', privacy_agreement_date);
      }

      try {
        const response = await axios.post(path, formData);

        if (response) {
          if (response.data.code == 'success') {
            const n = {
              user_id: response.data.user_id,
              e_mail: response.data.e_mail,
              user_name: response.data.user_name,
              tel: response.data.tel,
              subscription: response.data.subscription,
              is_confirmed: response.data.is_confirmed,
              is_admin: response.data.is_admin,
              company_name: response.data.company_name,
              privacy_agreement_date: response.data.privacy_agreement_date
            };
            commit('auth_success', {
              user_id: n.user_id,
              e_mail: n.e_mail,
              user_name: n.user_name,
              tel: n.tel,
              subscription: n.subscription,
              is_confirmed: n.is_confirmed,
              is_admin: n.is_admin,
              company_name: n.company_name,
              privacy_agreement_date: n.privacy_agreement_date
            });
          } else {
            return response;
          }
          return response;
        }
      } catch (error) {
        error.message = '[유저정보 수정]' + error.message;
        Sentry.captureException(error);
      }
    },
    userConfirmed({ commit, state }, { e_mail, confirm_code }) {
      const path = state.backend_host + '/confirm_user';

      const formData = new FormData();
      formData.append('e_mail', e_mail);
      formData.append('confirm_code', confirm_code);
      return axios.post(path, formData).then(response => {
        const r = response.data;

        if (response.data.code === 'success') {
          commit('auth_success', {
            user_id: r.user_id,
            e_mail: r.e_mail,
            user_name: r.user_name,
            tel: r.tell,
            subscription: r.subscription,
            is_confirmed: r.is_confirmed,
            is_admin: r.is_admin,
            company_name: r.company_name,
            privacy_agreement_date: r.privacy_agreement_date
          });
        }
        return response;
      });
    },
    updateLicenseInfo({ commit, state }) {
      authApi
        .get('/get_user_license_info')
        .then(response => {
          if (response.data.code) {
            commit('setLicenseInfo', {
              ...response.data.license_info,
              available_report_cnt_info: {
                ...response.data.available_report_cnt_info
              }
            });
          } else {
            commit('setLicenseInfo', null);
          }
        })
        .catch(error => {
          console.log(error);
          commit('setLicenseInfo', null);
          error.message = '[라이센스 정보 수정]' + error.message;
          Sentry.captureException(error);
        });
    },
    updateSelectedMethod({ commit, state }, e_mail) {
      authApi
        .post(`/payment/fetchPaymethodInfo`, {
          user_email: e_mail
        })
        .then(response => {
          if (response.data.code && response.data.methods) {
            if (response.data.methods.length > 0) {
              commit('setSelectedMethod', response.data.methods[0].item);
            } else {
              commit('setSelectedMethod', null);
            }
          }
        })
        .catch(error => {
          commit('setSelectedMethod', null);
          error.message = '[결제방식 수정]' + error.message;
          Sentry.captureException(error);
        });
    }
  },
  plugins: [
    createPersistedState({
      key: 'vuex_local',
      paths: LOCAL_KEYS,
      storage: window.localStorage
    }),
    store => {
      store.subscribe(async (_, state) => {
        if (isRestoring) return; // ✅ 복원 중일 때 저장 막기

        for (const k of INDEXED_KEYS) {
          if (state[k] !== undefined) {
            try {
              const safe = stripFunctions(cloneDeep(state[k]));
              await idbSet(`vuex_indexed__${k}`, safe);
            } catch (err) {
              console.warn(`❌ IndexedDB 저장 실패: '${k}'`, err);
              Sentry.captureException(err);
            }
          }
        }
      });
    }
  ]
});

if (typeof localStorage !== 'undefined' && localStorage.getItem('vuex_state')) {
  // 기존 localStorage 제거
  localStorage.removeItem('vuex_state');
}

function stripFunctions(obj) {
  if (Array.isArray(obj)) {
    return obj.map(stripFunctions);
  } else if (obj && typeof obj === 'object') {
    const clean = {};
    for (const key in obj) {
      const val = obj[key];
      if (typeof val !== 'function') {
        clean[key] = stripFunctions(val);
      }
    }
    return clean;
  }
  return obj;
}

// ✅ IndexedDB 복원
// ✅ IndexedDB 복원 - 지연 방식
(async () => {
  const deletionFlagKey = 'ert_version';
  const deletionThreshold = 0.3;

  try {
    // ✅ 삭제 조건 확인
    const deletionFlagValue = parseFloat(localStorage.getItem(deletionFlagKey));
    const shouldDelete = isNaN(deletionFlagValue) || deletionFlagValue < deletionThreshold;

    if (shouldDelete) {
      const deletionTargets = [
        'business_cases',
        'category',
        'data_tables',
        'documents',
        'charts',
        'gri2021_indicators',
        'gri2021_report_view',
        'report_contents',
        'report_indicators',
        'story_board'
      ];

      for (const k of deletionTargets) {
        try {
          await idbDel(`vuex_indexed__${k}`);
        } catch (err) {
          Sentry.captureException(err);
        }
      }

      localStorage.setItem(deletionFlagKey, deletionThreshold.toString());
    }
  } catch (err) {
    console.error('[삭제 로직 오류]', err);
    Sentry.captureException(err);
  }

  // ✅ 지연 후 복원 시작 (200ms 지연)
  setTimeout(async () => {
    try {
      const restored = {};

      for (const k of INDEXED_KEYS) {
        try {
          const rawValue = await idbGet(`vuex_indexed__${k}`);

          if (rawValue !== undefined) {
            const value = cloneDeep(rawValue);
            if (k === 'report_list') {
              delete value.viewer_report_code;
            }

            // ✅ 현재 상태 확인 후 조건부 복원
            const currentState = store.state[k];
            const shouldRestore =
              !currentState ||
              (Array.isArray(currentState) && currentState.length === 0) ||
              (typeof currentState === 'object' &&
                currentState !== null &&
                Object.keys(currentState).length === 0);

            if (shouldRestore) {
              restored[k] = value;
            } else {
            }
          }
        } catch (err) {
          console.warn(`❌ IndexedDB 복원 실패: '${k}'`, err);
          Sentry.captureException(err);
        }
      }

      // ✅ 복원할 데이터가 있을 때만 상태 업데이트
      if (Object.keys(restored).length > 0) {
        const merged = merge({}, store.state, restored);
        store.replaceState(merged);
      } else {
      }
    } catch (e) {
      console.error('[IndexedDB 복원 실패]', e);
      Sentry.captureException(e);
    } finally {
      store.commit('setRestoring', false);
      isRestoring = false; // ✅ 복원 완료 후 저장 허용
    }
  }, 200); // 200ms 지연
})();
export default store;
